import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { Component_MFH } from "../../services/component_mfh";
import { CalcTemplate } from "../../services/calctemplate";
import { FormControlsEventProxyService } from "../../services/formcontrols_eventproxy_service";
import { Utility } from "../../services/utility";
import { WarningModalComponent } from "../warning-modal/warning-modal.component";
import { ComponentTypeData } from "../models/component-type";
import { Component_MAWP } from "../../services/component_mawp";

@Component({
  selector: "mfh",
  templateUrl: "mfh.component.html",
  styleUrls: ["mfh.component.css"],
  standalone: false,
})
export class MFHComponent implements OnInit {
  public componentMFH = Component_MFH;
  public calcTemplate = CalcTemplate;
  private readonly mfhContextStorageKey = "mfh_context_class";

  mfhForm: FormGroup;
  intUnit: number;
  validationList = [];
  validationListLabel = "Following inputs have invalid information.";

  ComponentTypeData = [{ value: "8", text: ComponentTypeData.API650AST }];
  api650Selected = false;

  @Output() summaryCalculated: EventEmitter<any> = new EventEmitter();
  @Output("nextStep") nextStep: EventEmitter<any> = new EventEmitter();

  constructor(
    private _formBuilder: FormBuilder,
    private dialog: MatDialog,
    private formControlsEventProxyService: FormControlsEventProxyService
  ) {}

  // Build the MFH form and initialize shared MFH state.
  ngOnInit(): void {
    this.resetMfhStateIfAssessmentChanged();

    const hasPersistedMfhState = this.hasPersistedMfhState();
    if (!hasPersistedMfhState) {
      Component_MFH.MFH_initialize();
    }

    // this.mfhForm = this._formBuilder.group({
    //   selectedComponentType: ["", Validators.required],
    //   insideDiameter_MFH: [null, Validators.required],
    //   nominalThickness_MFH: [null, Validators.required],
    //   internalLoss_MFH: [null, Validators.required],
    //   externalLoss_MFH: [null, Validators.required],
    //   circumferentialWeldJointEfficiency_MFH: [null, Validators.required],
    //   allowableStress_MFH: [null, Validators.required],
    //   designLiquidHead_MFH: [null, Validators.required],
    //   specificGravity_MFH: [null, Validators.required],
    //   allowableRemainingStrengthFactor_MFH: [0.9, Validators.required],
    //   shellCourseBottomHeight_MFH: [null, Validators.required],
    //   internalFca_MFH: [null, Validators.required],
    //   externalFca_MFH: [null, Validators.required],

    this.mfhForm = this._formBuilder.group({
      selectedComponentType: ["8", Validators.required],
      insideDiameter_MFH: [null, Validators.required],
      nominalThickness_MFH: [null, Validators.required],
      internalLoss_MFH: [null, Validators.required],
      externalLoss_MFH: [null, Validators.required],
      circumferentialWeldJointEfficiency_MFH: [null, Validators.required],
      allowableStress_MFH: [null, Validators.required],
      designLiquidHead_MFH: [null, Validators.required],
      specificGravity_MFH: [null, Validators.required],
      shellCourseBottomHeight_MFH: [null, Validators.required],
      internalFca_MFH: [null, Validators.required],
      externalFca_MFH: [null, Validators.required],
    });

    this.intUnit = parseInt(localStorage.getItem("unitType")) || 0;
    Component_MFH.intUnit_select = this.intUnit;

    this.applyDefaultApi650AstInputs();
    if (hasPersistedMfhState) {
      this.hydrateFormFromComponentMfh();
    }

    this.formControlsEventProxyService.getMAWPEventSubject().subscribe(() => {
      if (this.hasPersistedMfhState()) {
        this.hydrateFormFromComponentMfh();
      }
    });
  }

  private hasPersistedMfhState(): boolean {
    return Number(Component_MFH.intEquip) === 8 && (
      Number(Component_MFH.sngID) > 0 ||
      Number(Component_MFH.sngtnom) > 0 ||
      Number(Component_MFH.sngS_stress) > 0 ||
      Number(Component_MFH.sngH_L) > 0
    );
  }

  private resetMfhStateIfAssessmentChanged(): void {
    const currentClass = (Component_MAWP.strD_class || "").toUpperCase();
    const previousClass = (sessionStorage.getItem(this.mfhContextStorageKey) || "").toUpperCase();
    const preserveImportedGmlTank =
      currentClass === "GML" &&
      Number(localStorage.getItem("TypeAradioselection")) === 7 &&
      this.hasPersistedMfhState();

    if (
      previousClass &&
      currentClass &&
      previousClass !== currentClass &&
      !preserveImportedGmlTank
    ) {
      Component_MFH.MFH_initialize();
    }

    if (currentClass) {
      sessionStorage.setItem(this.mfhContextStorageKey, currentClass);
    }
  }

  private hydrateFormFromComponentMfh(): void {
    const setIfFinite = (controlName: string, value: number) => {
      if (Number.isFinite(Number(value)) && value >= 0) {
        this.mfhForm.get(controlName)?.setValue(value);
      }
    };

    this.mfhForm.get("selectedComponentType")?.setValue("8");
    this.api650Selected = true;
    Component_MFH.intEquip = 8;
    this.applyApi650FieldRules();

    setIfFinite("insideDiameter_MFH", Number(Component_MFH.sngID));
    setIfFinite("nominalThickness_MFH", Number(Component_MFH.sngtnom));
    setIfFinite("internalLoss_MFH", Number(Component_MFH.sngLOSSi));
    setIfFinite("externalLoss_MFH", Number(Component_MFH.sngLOSSe));
    setIfFinite("internalFca_MFH", Number(Component_MFH.sngFCAi));
    setIfFinite("externalFca_MFH", Number(Component_MFH.sngFCAe));
    setIfFinite("allowableStress_MFH", Number(Component_MFH.sngS_stress));
    setIfFinite("circumferentialWeldJointEfficiency_MFH", Number(Component_MFH.sngE_c));
    setIfFinite("designLiquidHead_MFH", Number(Component_MFH.sngH_L));
    setIfFinite("specificGravity_MFH", Number(Component_MFH.sngs_g));
    setIfFinite("shellCourseBottomHeight_MFH", Number(Component_MFH.sngH_c));

    this.onInsideDiameterChanged(this.mfhForm.get("insideDiameter_MFH")?.value);
    this.onNominalThicknessChanged(this.mfhForm.get("nominalThickness_MFH")?.value);
    this.onInternalLossChanged(this.mfhForm.get("internalLoss_MFH")?.value);
    this.onExternalLossChanged(this.mfhForm.get("externalLoss_MFH")?.value);
    this.onInternalFcaChanged(this.mfhForm.get("internalFca_MFH")?.value);
    this.onExternalFcaChanged(this.mfhForm.get("externalFca_MFH")?.value);
    this.onAllowableStressChanged(this.mfhForm.get("allowableStress_MFH")?.value);
    this.onCircumferentialWeldJointEfficiencyChanged(this.mfhForm.get("circumferentialWeldJointEfficiency_MFH")?.value);
    this.onDesignLiquidHeadChanged(this.mfhForm.get("designLiquidHead_MFH")?.value);
    this.onSpecificGravityChanged(this.mfhForm.get("specificGravity_MFH")?.value);
    this.onShellCourseBottomHeightChanged(this.mfhForm.get("shellCourseBottomHeight_MFH")?.value);
  }

  // Default state before a component type is selected.
  private setInitialState(): void {
    this.api650Selected = false;
    this.disableAllInputFields();
    CalcTemplate.sngRSF_a = 0.9;
  }

  // Disable all editable MFH controls (keep component selector enabled).
  private disableAllInputFields(): void {
    Object.keys(this.mfhForm.controls)
      .filter((controlName) => controlName !== "selectedComponentType")
      .forEach((controlName) => this.mfhForm.controls[controlName].disable({ emitEvent: false }));
  }

  // Enable only API 650 fields used in this MFH workflow.
  private applyApi650FieldRules(): void {
    this.disableAllInputFields();

    const enabledControls = [
      "insideDiameter_MFH",
      "nominalThickness_MFH",
      "internalLoss_MFH",
      "externalLoss_MFH",
      "internalFca_MFH",
      "externalFca_MFH",
      "allowableStress_MFH",
      "circumferentialWeldJointEfficiency_MFH",
      "designLiquidHead_MFH",
      "specificGravity_MFH",
      //"allowableRemainingStrengthFactor_MFH",
      "shellCourseBottomHeight_MFH",
    ];

    enabledControls.forEach((controlName) => {
      this.mfhForm.controls[controlName]?.enable({ emitEvent: false });
    });

    CalcTemplate.sngRSF_a = 0.9;
  }

  private applyDefaultApi650AstInputs(): void {
    this.api650Selected = true;
    Component_MFH.intEquip = 8;
    this.applyApi650FieldRules();

    this.onInsideDiameterChanged(this.mfhForm.get("insideDiameter_MFH")?.value);
    this.onNominalThicknessChanged(this.mfhForm.get("nominalThickness_MFH")?.value);
    this.onInternalLossChanged(this.mfhForm.get("internalLoss_MFH")?.value);
    this.onExternalLossChanged(this.mfhForm.get("externalLoss_MFH")?.value);
    this.onInternalFcaChanged(this.mfhForm.get("internalFca_MFH")?.value);
    this.onExternalFcaChanged(this.mfhForm.get("externalFca_MFH")?.value);
    this.onAllowableStressChanged(this.mfhForm.get("allowableStress_MFH")?.value);
    this.onCircumferentialWeldJointEfficiencyChanged(this.mfhForm.get("circumferentialWeldJointEfficiency_MFH")?.value);
    this.onDesignLiquidHeadChanged(this.mfhForm.get("designLiquidHead_MFH")?.value);
    this.onSpecificGravityChanged(this.mfhForm.get("specificGravity_MFH")?.value);
    this.onShellCourseBottomHeightChanged(this.mfhForm.get("shellCourseBottomHeight_MFH")?.value);
  }

  // Toggle UI/form behavior based on selected component type.
  onComponentTypeChange(numValue: string): void {
    const componentTypeNumber = parseInt(numValue, 10);

    this.api650Selected = componentTypeNumber === 8;
    Component_MFH.intEquip = componentTypeNumber;
    console.log("Selected Component Type:", this.api650Selected ? "API 650 AST" : "Other");

    if (this.api650Selected) {
      this.applyApi650FieldRules();
    } else {
      this.setInitialState();
    }
  }

  // Validate -> optionally alert -> calculate -> emit summary and go next.
  calculateSummary(): void {
    Object.keys(this.mfhForm.controls).forEach((key) => {
      this.mfhForm.get(key).markAsDirty();
    });

    const valid = Component_MFH.calc_Check(this.mfhForm);
    this.validationList = Component_MFH.validationMessage;
    if (!valid) {
      console.warn("[MFH] Validation failed; staying on Component MFH step", this.validationList);
      this.dialog.open(WarningModalComponent, {
        width: "600px",
        data: {
          warningMessage: this.validationList,
          caption: "MFH: Input Validation",
          captionCenter: false,
          cancelButtonShow: false,
        },
      });
      return;
    }

    if (Component_MFH.alertMessage.length > 0) {
      this.dialog.open(WarningModalComponent, {
        width: "600px",
        data: {
          warningMessage: Component_MFH.alertMessage,
          caption: "Alert",
          captionCenter: false,
          cancelButtonShow: false,
        },
      });
    }

    const results = Component_MFH.MFH_calc();
    console.log("[MFH] Summary output", results);
    this.summaryCalculated.emit(results);
    this.nextStep.emit();
  }

  // Input change handlers map UI fields into MFH/CalcTemplate state.
  onInsideDiameterChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngID = Number(e);
  }

  onNominalThicknessChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngtnom = Number(e);
  }

  onInternalLossChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngLOSSi = Number(e);
  }

  onExternalLossChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngLOSSe = Number(e);
  }

  onInternalFcaChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngFCAi = Number(e);
  }

  onExternalFcaChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngFCAe = Number(e);
  }

  onAllowableStressChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngS_stress = Number(e);
  }

  onCircumferentialWeldJointEfficiencyChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngE_c = Number(e);
  }

  onDesignLiquidHeadChanged(e) {
    if (Utility.isTextADouble(e)) {
      Component_MFH.sngH_L = Number(e);
      if (Component_MFH.sngH_L === 0) {
        this.mfhForm.get("specificGravity_MFH")?.disable();
        this.mfhForm.get("specificGravity_MFH")?.setValue("");
      } else {
        this.mfhForm.get("specificGravity_MFH")?.enable();
      }
    }
  }

  onSpecificGravityChanged(e) {
    if (Utility.isTextADouble(e)) Component_MFH.sngs_g = Number(e);
  }

  onAllowableRemainingStrengthFactorChanged(e) {
    if (Utility.isTextADouble(e)) CalcTemplate.sngRSF_a = Number(e);
    else CalcTemplate.sngRSF_a = 0.9;
  }

  onShellCourseBottomHeightChanged(e) {
    if (Utility.isTextADouble(e)) {
      Component_MFH.sngH_c = Number(e);
      CalcTemplate.sngH_f = Number(e);
    }

    this.formControlsEventProxyService.triggerMfhShellCourseBottomHeightEvent();
  }

  onWeldAtThicknessTransitionChanged(e) {
    if (Utility.isTextADouble(e)) {
      Component_MFH.sngH_w = Number(e);
    }
  }
}
